import getEnv from '../environment/resolve';

/**
 * Preview URLs arrive from the CMS as absolute *production* URLs — there is a
 * single prod Strapi instance shared by every environment (`scripts/src/cms.js`),
 * and `previewURL` is copied out of it verbatim. Two separate things then have to
 * be corrected for a preview on dashboard.dev to talk to dev. (TECH-320)
 *
 * 1. The iframe origin — `withAppOrigin`. The app build is byte-identical on
 *    every host, so this does NOT by itself change which backend the app picks
 *    (see below). It matters because the app calls Sherlock with
 *    `credentials: 'include'`: serving the app from dev.myf2b.com keeps that
 *    request same-site with dev Sherlock, where serving it from myf2b.com would
 *    make it cross-site and expose the cookie to SameSite restrictions.
 *
 * 2. The backend the app selects — `appEnvName`. The app's bootstrap tries
 *    `parentWindow.origin`, which throws for a cross-origin parent, so its
 *    `secureURL` config is always null and `AppSettings` keeps its declared
 *    production default. Its supported override is a `_bookbuddies_env` param
 *    read out of `intent_url`, which sets `AppSettings.environment` and drives
 *    `updateSecureUrl()`. Verified live: on dev.myf2b.com `secureURL` reads
 *    `https://myf2b.com` until `environment` is set, then `https://dev.myf2b.com`.
 *
 * Only the app hosts are rewritten, via an explicit allowlist rather than a
 * `.myf2b.com` suffix match. Other assets reached through `previewURL` —
 * printable PDFs and videos, currently on f2b-cms.s3.amazonaws.com — are served
 * from a single origin in every environment and must be left untouched. The
 * allowlist keeps that true whichever host the CMS emits next.
 */
const APP_HOSTS = ['myf2b.com', 'www.myf2b.com', 'stage.myf2b.com', 'dev.myf2b.com'];

const withAppOrigin = (url) => {
	const Url = new URL(url);
	if(!APP_HOSTS.includes(Url.host)){ return Url; }
	const appHost = new URL(getEnv().appHost);
	Url.protocol = appHost.protocol;
	Url.host = appHost.host;
	return Url;
};

/**
 * The `_bookbuddies_env` value for the current environment, or null on
 * production. The app builds its host as `'https://' + env + '.myf2b.com'`, so
 * there is no prod value — an absent param correctly leaves the production
 * default in place.
 */
export const appEnvName = () => {
	const { host } = new URL(getEnv().appHost);
	if(host === 'myf2b.com' || host === 'www.myf2b.com'){ return null; }
	return host.split('.')[0] || null;
};

export default withAppOrigin;
